import * as React from "react"
import { router } from "@inertiajs/react"
import { RefreshCw, Save } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { formatInboxDate } from "./helpers"
import { Input } from "@/components/ui/input"
import { RichTextField } from "@/components/ui/rich-text-field"
import { Select } from "@/components/ui/select"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import type { ReplyTemplate, SelectedReply } from "./types"

type DraftComposerProps = {
  readonly reply: SelectedReply
  readonly bodyHtml: string
  readonly onBodyHtmlChange: (value: string) => void
}

export function DraftComposer({ reply, bodyHtml, onBodyHtmlChange }: DraftComposerProps) {
  const [feedback, setFeedback] = React.useState("")
  const [templates, setTemplates] = React.useState<readonly ReplyTemplate[]>([])
  const [templateId, setTemplateId] = React.useState("")
  const [contentEpoch, setContentEpoch] = React.useState(0)

  // Hold an auto-reload block while the feedback box has content, mirroring the
  // RichTextField body editor. Without this, unsaved feedback is destroyed by the
  // standalone-recovery reload (pageshow / visibility-return).
  useBlockAutoReload(feedback.trim().length > 0)

  // Depend on the client *id* (a primitive), NOT the client object. The inbox
  // polls every 10s (Inbox.tsx: router.reload) which hands us a fresh `reply`
  // object each cycle; keying on `reply.client` re-ran this effect on every poll
  // and wiped in-progress feedback (setFeedback("")) — so feedback typed over
  // more than ~10s vanished. Primitive deps only re-run on a genuine reply switch.
  const clientId = reply.client?.id ?? null
  React.useEffect(() => {
    setFeedback("")
    setTemplateId("")
    if (clientId == null) {
      setTemplates([])
      return
    }

    const url = new URL("/api/templates", window.location.origin)
    url.searchParams.set("client_id", String(clientId))
    if (reply.classification) url.searchParams.set("intent", reply.classification)

    let active = true
    fetch(url)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error("Template fetch failed"))))
      .then((data: { readonly templates?: readonly ReplyTemplate[] }) => {
        if (active) setTemplates(data.templates ?? [])
      })
      .catch((error: unknown) => {
        if (error instanceof Error && active) setTemplates([])
      })
    return () => {
      active = false
    }
  }, [reply.id, clientId, reply.classification])

  const saveDraft = () => {
    router.post(`/replies/${reply.id}/draft`, { body_html: bodyHtml }, { preserveScroll: true })
  }

  const regenerate = () => {
    // Send the body on screen too: feedback should EDIT this draft, not trigger a
    // rewrite from scratch that discards whatever the operator already fixed.
    router.post(
      `/replies/${reply.id}/regenerate`,
      { feedback, body_html: bodyHtml },
      { preserveScroll: true },
    )
  }

  const insertTemplate = (id: string) => {
    setTemplateId(id)
    const template = templates.find((candidate) => String(candidate.id) === id)
    if (!template) return
    onBodyHtmlChange(bodyHtml ? `${bodyHtml}<br><br>${template.body_html}` : template.body_html)
    // Force the editor to remount so it picks up the inserted template — the draft
    // id is unchanged, and the editor only reads defaultValue at mount.
    setContentEpoch((epoch) => epoch + 1)
  }

  const isProcessing = reply.drafting_job?.status === "queued" || reply.drafting_job?.status === "claimed" || reply.drafting_job?.status === "drafting"

  return (
    <section className="rounded-lg border border-hairline bg-page p-4">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="min-w-48 flex-1 space-y-2">
          Canned reply
          <Select value={templateId} onChange={(event) => insertTemplate(event.target.value)}>
            <option value="">Choose template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.label}
              </option>
            ))}
          </Select>
        </label>
      </div>
      {/*
        `revision` is part of the key on purpose. RichTextField ignores a changed
        defaultValue after mount, so keying on draft id alone left the editor
        showing revision N while the parent's bodyHtml had already moved to N+1 —
        then one keystroke re-emitted the stale body over the new one, and the
        operator could approve and send superseded content.
      */}
      <RichTextField
        key={`${reply.active_draft?.id ?? `reply-${reply.id}`}-${reply.active_draft?.revision ?? 0}-${contentEpoch}`}
        defaultValue={bodyHtml}
        onChange={onBodyHtmlChange}
        placeholder="Write the reply..."
        className="min-h-64"
      />
      {reply.drafting_job?.status === "failed" && (
        <p className="text-ink-muted mt-2 text-sm">
          Last redraft failed: {reply.drafting_job.last_error}
        </p>
      )}
      {isProcessing && (
        <p className="text-ink-muted mt-2 text-sm">Redrafting&hellip;</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" onClick={saveDraft}>
          <Save className="h-4 w-4" />
          Save draft
        </Button>
        {reply.active_draft && (
          reply.active_draft.review_status === "approved" ? (
            <Badge tone="signal">
              {reply.active_draft.approved_at
                ? `Approved ${formatInboxDate(reply.active_draft.approved_at)}`
                : "Approved"}
            </Badge>
          ) : (
            <Badge tone="neutral">Not sent yet</Badge>
          )
        )}
        {(reply.active_draft?.revision ?? 0) > 1 && (
          <span className="text-sm text-ink-muted">
            Draft version {reply.active_draft?.revision}
          </span>
        )}
        <div className="ml-auto flex">
          <Input
            value={feedback}
            onChange={(event) => setFeedback(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                if (!isProcessing) regenerate()
              }
            }}
            placeholder='Revision instructions — e.g. "shorten it, drop pricing, mention their Shopify migration"'
            maxLength={2000}
            className="w-64 rounded-r-none border-r-0"
          />
          <Button type="button" variant="secondary" onClick={regenerate} disabled={isProcessing} className="rounded-l-none">
            <RefreshCw className="h-4 w-4" />
            Regenerate
          </Button>
        </div>
      </div>
    </section>
  )
}
